/**
 * Plan: the collections as they are driven now, per day and vehicle (16 October 2026).
 *
 * Ronald, 16 October: "make sure to only show actual plans that are currently in operation for now, the
 * optimisation we can work on over the weekend". So Plan is not an optimiser run: each farm is collected on
 * the days of its last tickets, by the vehicle on those tickets, and a vehicle that collects nothing (a hired
 * cab that pulls no tanker) is not on it. The figures are the same as Milk Flow's: the forecast's litres,
 * the barrels filled from the loading day's own J.H. Willis farms (MF-16), and the times the plant's
 * meter usually sees each tanker unload. The optimiser's runs stay at /runs, out of the navigation.
 *
 * The form is the platform's: a page header with its period, a titled card per day, tables of numbers, and
 * the explanation in the Explainer at the foot.
 */
import { Explainer, PageHeader, Section, Table } from "@depicomnlbv/platform-ui";
import { Link, useSearchParams } from "react-router-dom";

import {
  SECTION_NAMES,
  useMilkFlowAlerts,
  useOperatingPlan,
  type OperatingPlan,
  type PlanDay,
  type PlanVehicle,
} from "../api/milkFlow";
import { Failure, Pending } from "../components/Failure";
import { AlertsLine } from "../components/MilkFlowAlerts";
import { addDays, formatLitres, isCalendarDate } from "../time";
import { useTitle } from "../useTitle";
import { Figure } from "./MilkFlow";
import "./milkFlow.css";

const DAYS = 7;
const WEEKDAY = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "long", day: "numeric", month: "long" });
const SHORT = new Intl.DateTimeFormat("en-GB", { timeZone: "UTC", weekday: "short", day: "numeric" });
const dayTitle = (d: string) => WEEKDAY.format(new Date(`${d}T00:00:00Z`));
const dayShort = (d: string) => SHORT.format(new Date(`${d}T00:00:00Z`));
const litres = (n: number) => formatLitres(n).replace(" L", " L");
const none = <span className="nothing">—</span>;

const VEHICLE: Record<PlanVehicle["vehicle"], string> = { own: "Our tanker", willis: "J.H. Willis" };

function DayTable({ d }: { d: PlanDay }) {
  return (
    <div className="mf-scroll">
      <Table caption={`The collections on ${dayTitle(d.day)}`} captionHidden className="milk-flow-table op-plan">
        <thead>
          <tr>
            <th scope="col">Vehicle</th>
            <th scope="col">Farms</th>
            <th className="dpc-table__num" scope="col">Litres</th>
            <th className="dpc-table__num" scope="col">To the barrels</th>
            <th className="dpc-table__num" scope="col">To Gwalchmai</th>
            <th className="dpc-table__num" scope="col">At Gwalchmai</th>
          </tr>
        </thead>
        <tbody>
          {d.vehicles.map((v) => (
            <tr key={v.vehicle}>
              <th scope="row">
                {VEHICLE[v.vehicle]}
                {v.plates.length > 0 ? <span className="mf-cell-sub">{v.plates.join(", ")}</span> : null}
              </th>
              <td>
                <ul className="op-farms" role="list">
                  {v.farms.map((f) => (
                    <li key={f.farmer_id}>
                      <span>{f.farm_name}</span>
                      <Figure expected value={litres(f.litres)} />
                    </li>
                  ))}
                </ul>
              </td>
              <td className="dpc-table__num"><Figure expected value={litres(v.litres)} /></td>
              <td className="dpc-table__num">
                {v.to_barrels_litres > 0 ? <Figure expected value={litres(v.to_barrels_litres)} /> : none}
                {v.vehicle === "willis" && d.barrels && d.barrels.short_litres > 0 ? (
                  <span className="mf-cell-sub">
                    <Figure short value={`−${litres(d.barrels.short_litres)}`} />
                  </span>
                ) : null}
              </td>
              <td className="dpc-table__num">{v.to_gwalchmai_litres > 0 ? <Figure expected value={litres(v.to_gwalchmai_litres)} /> : none}</td>
              <td className="dpc-table__num">{v.at_gwalchmai.length > 0 ? v.at_gwalchmai.join(", ") : none}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function HowItIsWorkedOut({ p }: { p: OperatingPlan }) {
  const t = p.unload_times;
  return (
    <Explainer summary="How this plan is worked out">
      <dl className="explainer-list">
        <dt>The farms</dt>
        <dd>
          Each farm is collected every other day, on the days of its last three tickets, by the vehicle on those
          tickets. A vehicle that has no ticket in the last 7 days is not on the plan. The plates beside a vehicle are
          the ones on its tickets of the last 7 days, most used first.
        </dd>
        <dt>The litres</dt>
        <dd>Expected, in italics: the forecast for the two days a collection takes.</dd>
        <dt>Barrels</dt>
        <dd>
          On a loading day J.H. Willis&rsquo;s farms of that day fill the barrels to the agreed litres first; the rest goes to
          Gwalchmai. A figure in red under it is what those farms cannot give against the agreement.
        </dd>
        <dt>At Gwalchmai</dt>
        <dd>
          The median time the plant&rsquo;s meter saw the tanker unload on the last three days of the kind: our tanker{" "}
          {t.own?.join(", ") ?? "—"}, J.H. Willis {t.willis?.join(" and ") ?? "—"} on a collection day, and{" "}
          {t.other?.join(", ") ?? "—"} on a day between. One time per tanker of up to 20,000 L.
        </dd>
        <dt>The optimiser&rsquo;s runs</dt>
        <dd>
          Not shown here until the optimisation is reworked. They stay at <Link to="/runs">Planning runs</Link>.
        </dd>
      </dl>
    </Explainer>
  );
}

export function OperatingPlanPage() {
  const [search] = useSearchParams();
  const asked = search.get("from") ?? undefined;
  const plan = useOperatingPlan(asked && isCalendarDate(asked) ? asked : undefined, DAYS);
  // The risks of the next 48 hours (AP-4); a caller who may not read them simply sees none.
  const alerts = useMilkFlowAlerts();
  useTitle("Plan");

  if (plan.isPending) return <Pending>Reading the collections</Pending>;
  if (plan.isError) return <Failure context={{ screen: "Plan" }} error={plan.error} />;
  const p = plan.data;
  const last = addDays(p.first, p.days - 1);
  const missing = p.unavailable.map((s) => SECTION_NAMES[s] ?? s).join(", ");
  return (
    <div className="page page--blocks mf-page">
      <PageHeader
        actions={<Link to="/milk-flow">Milk Flow</Link>}
        period={{
          label: "Days",
          current: `${dayShort(p.first)} to ${dayShort(last)}`,
          previous: p.first > p.today ? <Link to={`/plan?from=${addDays(p.first, -DAYS) < p.today ? p.today : addDays(p.first, -DAYS)}`}>Previous days</Link> : null,
          next: <Link to={`/plan?from=${addDays(p.first, DAYS)}`}>Next days</Link>,
        }}
        title="Plan"
      />
      {missing ? (
        <p className="milk-flow-notice" role="status">
          {missing}: not available yet. The database views this needs are not in place, or this application may not read them.
        </p>
      ) : null}
      <AlertsLine alerts={alerts.data?.alerts ?? []} />
      {p.days_plan.map((d) => (
        <Section
          empty={d.vehicles.length === 0 ? "No farm is collected on this day." : undefined}
          flush
          key={d.day}
          title={dayTitle(d.day)}
        >
          {d.vehicles.length > 0 ? <DayTable d={d} /> : null}
        </Section>
      ))}
      <HowItIsWorkedOut p={p} />
    </div>
  );
}
